import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneTilt, ChatCircleDots, Sparkle } from "@phosphor-icons/react";
import { COUNTRIES, COUNTRY_BY_SLUG } from "../lib/countries";
import { sendExpertMessage } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, breadcrumbLd } from "../components/Seo";
import { track } from "../lib/analytics";
import { toast, Toaster } from "sonner";

const STARTERS = [
  "How do I apply for the visa as an Indian passport holder?",
  "Cheapest week to fly here from Delhi/Mumbai in next 3 months?",
  "Best veg-friendly food spots and what to order?",
  "How do I get around — local SIM, transport apps, day passes?",
  "Smart budget hacks specifically for Indian travelers?",
];

export default function Experts() {
  const { country: slugParam } = useParams();
  const navigate = useNavigate();
  const [slug, setSlug] = useState(slugParam || "japan");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  const country = COUNTRY_BY_SLUG[slug];

  useEffect(() => {
    if (slugParam && slugParam !== slug) setSlug(slugParam);
  }, [slugParam]);

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
  }, [slug]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || loading) return;
    track("expert_message_sent", {
      country: slug,
      starter: !!text,
      length: msg.length,
    });
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendExpertMessage({
        country_slug: slug,
        session_id: sessionId,
        message: msg,
      });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Expert is unavailable");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-mobile-nav" data-testid="experts-page">
      <Seo
        title={
          country
            ? `${country.name} travel expert · visas, flights, food & hacks for Indians`
            : "AI travel experts for 12 countries — built for Indian travelers"
        }
        description={
          country
            ? `Chat with an AI ${country.name} travel expert tuned for Indian passport holders. Get visa, eSIM, veg-food, transport, and budget hacks — instantly.`
            : "Multi-turn chat with AI experts for Vietnam, Thailand, Bali, Japan, Singapore, Maldives, Oman, Malaysia, Sri Lanka, Nepal, Philippines, and Laos."
        }
        path={slugParam ? `/experts/${slugParam}` : "/experts"}
        image={country?.image}
        jsonLd={breadcrumbLd(
          country
            ? [
                { name: "Home", path: "/" },
                { name: "AI Experts", path: "/experts" },
                { name: country.name, path: `/experts/${country.slug}` },
              ]
            : [
                { name: "Home", path: "/" },
                { name: "AI Experts", path: "/experts" },
              ],
        )}
      />
      <Toaster theme="light" position="top-center" offset={80} />
      <Navbar />
      <div className="pt-24 max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="mb-6 sm:mb-8">
          <span className="kicker">— AI Country Experts</span>
          <h1 className="font-serif text-3xl sm:text-6xl mt-3 text-slate-900">
            Thirteen experts.{" "}
            <span className="italic" style={{ color: "#2A7FB8" }}>
              Local-grade advice.
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar - country list (vertical on desktop, horizontal-scroll on mobile) */}
          <aside
            data-testid="expert-sidebar"
            className="lg:col-span-3 card-surface rounded-2xl p-3 lg:max-h-[70vh] lg:overflow-y-auto scroll-thin flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:overflow-x-visible scroll-snap-x"
          >
            {COUNTRIES.map((c) => (
              <button
                key={c.slug}
                data-testid={`expert-pick-${c.slug}`}
                onClick={() => {
                  setSlug(c.slug);
                  navigate(`/experts/${c.slug}`);
                }}
                className={`flex-shrink-0 lg:flex-shrink lg:w-full flex flex-col lg:flex-row items-center lg:items-center gap-2 lg:gap-3 p-2 rounded-xl text-left transition ${
                  slug === c.slug
                    ? "bg-[#EAF4FB] ring-1 ring-[#2A7FB8]/35"
                    : "hover:bg-slate-50"
                }`}
              >
                <img
                  src={c.image}
                  alt=""
                  className="w-14 h-14 lg:w-10 lg:h-10 rounded-xl lg:rounded-lg object-cover"
                />
                <div className="flex flex-col items-center lg:items-start">
                  <span className="text-xs lg:text-sm font-semibold text-slate-900 whitespace-nowrap">
                    {c.name}
                  </span>
                  <span className="hidden lg:inline text-[10px] uppercase tracking-widest text-slate-500">
                    {c.visa}
                  </span>
                </div>
              </button>
            ))}
          </aside>

          {/* Chat */}
          <section
            data-testid="expert-chat"
            className="lg:col-span-9 card-surface rounded-2xl flex flex-col"
            style={{ minHeight: "70vh" }}
          >
            <header className="flex items-center gap-4 p-5 border-b border-slate-200">
              <div className="relative">
                <img
                  src={country?.image}
                  alt={country?.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-md"
                />
                <span
                  className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center ring-2 ring-white"
                  style={{ background: "#2A7FB8" }}
                >
                  <Sparkle size={10} color="#ffffff" weight="fill" />
                </span>
              </div>
              <div>
                <div className="font-serif text-2xl leading-tight text-slate-900">
                  {country?.name} Expert
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  <span className="text-xs font-medium text-emerald-600">
                    Online
                  </span>
                </div>
              </div>
            </header>

            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto p-5 space-y-4 scroll-thin"
            >
              {messages.length === 0 && (
                <div className="space-y-6" data-testid="chat-starters">
                  <div className="flex gap-3 items-start max-w-2xl">
                    <span className="w-8 h-8 rounded-full grid place-items-center bg-[#EAF4FB]">
                      <ChatCircleDots
                        size={16}
                        weight="duotone"
                        color="#2A7FB8"
                      />
                    </span>
                    <div className="text-sm text-slate-700 leading-relaxed">
                      Namaste — I&apos;m your dedicated {country?.name} expert.
                      Ask me anything from visa paperwork to where to find the
                      best street pho. Try a starter below.
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STARTERS.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`starter-${i}`}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-2 rounded-full border border-slate-200 bg-white hover:border-[#2A7FB8]/60 hover:text-[#2A7FB8] text-slate-700"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <AnimatePresence initial={false}>
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      data-testid={`chat-msg-${m.role}`}
                      className={`max-w-[85%] rounded-2xl p-4 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "text-white shadow-md shadow-sky-200/60"
                          : "border border-slate-200 text-slate-800 bg-[#FDF8EE]"
                      }`}
                      style={
                        m.role === "user"
                          ? { background: "#2A7FB8" }
                          : undefined
                      }
                    >
                      {m.content}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {loading && (
                <div
                  data-testid="chat-typing"
                  className="flex justify-start"
                >
                  <div className="px-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm text-slate-500 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#2A7FB8] animate-pulse" />
                    Expert is thinking…
                  </div>
                </div>
              )}
            </div>

            <form
              data-testid="chat-form"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-slate-200 p-4 flex items-center gap-3 bg-[#FDF8EE]/60"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Ask the ${country?.name} expert…`}
                className="flex-1 input-base h-12"
                enterKeyHint="send"
                autoComplete="off"
                disabled={loading}
              />
              <button
                data-testid="chat-send"
                type="submit"
                disabled={loading || !input.trim()}
                className="h-12 w-12 rounded-full grid place-items-center disabled:opacity-40 shadow-md shadow-orange-200/70"
                style={{ background: "#FF7E4D" }}
              >
                <PaperPlaneTilt size={18} color="#ffffff" weight="fill" />
              </button>
            </form>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
